/**
 * Typed authenticated client for clinical-trials dashboard APIs.
 */

import { api } from "@risalabs_frontend_org/api-client";
import { getBearerToken } from "@risalabs_frontend_org/auth-wrapper";
import { API_ENDPOINTS } from "../enums/apiUrls";
import type {
  ClinicalNoteResponse,
  CreateNoteRequest,
  CriteriaEvaluationAnalyzeRequest,
  DocumentSummaryResponse,
  EligibilityAnalyzeResponse,
  EligibilityBookmarkResponse,
  EligibilityReadStateResponse,
  EligibilityNoteNotificationsClearedResponse,
  EligibilityNoteNotificationsResponse,
  EligibilitySourceResponse,
  EligibilityPatchRequest,
  EligibilityScreeningHistory,
  EligibilityPatchResponse,
  EligibilityRowsRequest,
  EligibilityRowsResponse,
  FacetCountsResponse,
  JobAcceptedResponse,
  JobHistoryResponse,
  JobMrnOutcomePage,
  JobStatusResponse,
  MarkNotificationReadRequest,
  MarkNotificationReadResponse,
  MarkNotificationUnreadResponse,
  MonitoringTrialResponse,
  NotificationsRequest,
  NotificationsResponse,
  NoteAssigneesResponse,
  NoteReplyRequest,
  NotesListResponse,
  NotesQuery,
  PatientDetailResponse,
  PatientCsvUploadOptions,
  PatientTagsUpdate,
  PdfUrlResponse,
  RunSummarizationRequest,
  TabCountsResponse,
  UpdateNoteRequest,
  ProtocolAuditEvent,
  ProtocolConnection,
  ProtocolCriterion,
  ProtocolDraft,
  ProtocolDraftBundle,
  ProtocolImportPreview,
  ProtocolTrial,
  ProtocolValidationRun,
  ProtocolVersion,
  ScreeningBatchResponse,
  ScreeningLogEvent,
  ScreeningRunRequest,
  ScreeningRunResponse,
  ScreeningRunResults,
  ScreeningRunStatus,
  ScreeningWorkflowDetail,
  ScreeningWorkflowSummary,
  T1AtomEvaluation,
  T1EvaluationRun,
  T1FunnelSnapshot,
  T1PatientEvaluation,
} from "./clinicalTrialsApiTypes";
import type { EligibilityOptionsResponse } from "../data-model/eligibilityOptions";
import type {
  CriteriaScreeningResponse,
  CreateEligibilityTaskRequest,
  CreateEligibilityTaskResponse,
  EligibilityDetailResponse,
  PatientStatusHistoryResponse,
  ScreeningOverride,
} from "../data-model/eligibilityDetail";
import type {
  EligibilityScreenedRowsRequest,
  EligibilityScreenedRowsResponse,
} from "../data-model/eligibilityRows";

/**
 * Camel-case filters accepted by the eligibility worklist client.
 */
export interface EligibilityScreenedRowsQuery {
  trialId: string;
  query?: string;
  tab?: string;
  patientStatus?: string;
  eligibilityStatus?: string;
  eligibilityTier?: string;
  additionalStudy?: string;
  stage?: string;
  diagnosisCode?: string;
  nextMdVisitFrom?: string;
  nextMdVisitTo?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  hitsPerPage?: number;
}

/**
 * Ergonomic (camelCase) query args accepted by `getNotifications`, mapped to the
 * unified-search POST body. `page` is 0-indexed; `facetFilters` maps a facet key to its
 * allowed values (e.g. `{ patient_status: ["Active"] }`).
 */
export interface NotificationsQuery {
  date: string;
  docType?: string;
  query?: string;
  facetFilters?: Record<string, string[]>;
  nextAppointmentFrom?: string;
  nextAppointmentTo?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  hitsPerPage?: number;
}

/**
 * Map the camelCase query args to the API's snake_case unified-search body, omitting
 * unset optional fields so the backend defaults apply.
 *
 * @param q - Notification query args
 * @returns The `NotificationsRequest` body to POST
 */
function buildNotificationsBody(q: NotificationsQuery): NotificationsRequest {
  const body: NotificationsRequest = { date: q.date };
  if (q.docType) body.doc_type = q.docType;
  if (q.query) body.query = q.query;
  if (q.facetFilters) body.facet_filters = q.facetFilters;
  if (q.nextAppointmentFrom) {
    body.next_appointment_from = q.nextAppointmentFrom;
  }
  if (q.nextAppointmentTo) body.next_appointment_to = q.nextAppointmentTo;
  if (q.sortBy) body.sort_by = q.sortBy;
  if (q.sortDir) body.sort_dir = q.sortDir;
  if (q.page !== undefined) body.page = q.page;
  if (q.hitsPerPage !== undefined) body.hits_per_page = q.hitsPerPage;
  return body;
}

/**
 * Map eligibility worklist query options to the backend's snake-case body.
 *
 * @param query - Trial, tab, filters, sort, and pagination state
 * @returns Request body for `POST /eligibility/screened-rows`
 */
function buildEligibilityScreenedRowsBody(
  query: EligibilityScreenedRowsQuery,
): EligibilityScreenedRowsRequest {
  const body: EligibilityScreenedRowsRequest = { trial_id: query.trialId };
  if (query.query) body.query = query.query;
  if (query.tab) body.tab = query.tab;
  if (query.patientStatus) body.patient_status = query.patientStatus;
  if (query.eligibilityStatus) {
    body.eligibility_status = query.eligibilityStatus;
  }
  if (query.eligibilityTier) body.eligibility_tier = query.eligibilityTier;
  if (query.additionalStudy) body.additional_study = query.additionalStudy;
  if (query.stage) body.stage = query.stage;
  if (query.diagnosisCode) body.diagnosis_code = query.diagnosisCode;
  if (query.nextMdVisitFrom) {
    body.next_md_visit_from = query.nextMdVisitFrom;
  }
  if (query.nextMdVisitTo) body.next_md_visit_to = query.nextMdVisitTo;
  if (query.sortBy) body.sort_by = query.sortBy;
  if (query.sortDir) body.sort_dir = query.sortDir;
  if (query.page !== undefined) body.page = query.page;
  if (query.hitsPerPage !== undefined) {
    body.hits_per_page = query.hitsPerPage;
  }
  return body;
}

/**
 * Serialize populated notes filters without emitting false or empty values.
 *
 * @param query - Facility notes query options
 * @returns URL query string including the leading `?` when populated
 */
function buildNotesQuery(query: NotesQuery): string {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== false && value !== "") {
      params.set(key, String(value));
    }
  });
  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
}

/**
 * Authenticated client for the clinical-trials `/api/v1` dashboard endpoints.
 * Bearer token + correlation headers are auto-injected by the shared api-client.
 */
export const clinicalTrialsService = {
  /** Fetch the facility-wide notes inbox with server-side filters and ordering. */
  getNotes: (query: NotesQuery, signal?: AbortSignal) =>
    api.get<NotesListResponse>(
      `${API_ENDPOINTS.NOTES}${buildNotesQuery(query)}`,
      signal,
    ),

  /** Fetch active clinical-trials members eligible for assignment. */
  getNoteAssignees: (signal?: AbortSignal) =>
    api.get<NoteAssigneesResponse>(API_ENDPOINTS.NOTE_ASSIGNEES, signal),

  /** Fetch all collaboration notes retained for one patient. */
  getPatientNotes: (mrn: string, signal?: AbortSignal) =>
    api.get<NotesListResponse>(API_ENDPOINTS.PATIENT_NOTES(mrn), signal),

  /** Fetch one note with its complete activity stream. */
  getNote: (noteId: string, signal?: AbortSignal) =>
    api.get<ClinicalNoteResponse>(API_ENDPOINTS.NOTE_DETAIL(noteId), signal),

  /** Fetch the authenticated user's Notes notifications for one Eligibility row. */
  getEligibilityNotifications: (
    trialId: string,
    mrn: string,
    signal?: AbortSignal,
  ) =>
    api.get<EligibilityNoteNotificationsResponse>(
      API_ENDPOINTS.ELIGIBILITY_NOTIFICATIONS(trialId, mrn),
      signal,
    ),

  /** Mark one Eligibility Notes notification read for the current user. */
  markEligibilityNotificationRead: async (
    trialId: string,
    mrn: string,
    notificationId: string,
    signal?: AbortSignal,
  ): Promise<void> => {
    try {
      await api.post<Record<string, never>, void>(
        API_ENDPOINTS.ELIGIBILITY_NOTIFICATION_READ(
          trialId,
          mrn,
          notificationId,
        ),
        {},
        signal,
      );
    } catch (error: unknown) {
      if (!(error instanceof SyntaxError)) throw error;
    }
  },

  /** Mark all Eligibility Notes notifications for one patient read. */
  clearEligibilityNotifications: (
    trialId: string,
    mrn: string,
    signal?: AbortSignal,
  ) =>
    api.post<
      Record<string, never>,
      EligibilityNoteNotificationsClearedResponse
    >(API_ENDPOINTS.ELIGIBILITY_NOTIFICATIONS_CLEAR(trialId, mrn), {}, signal),

  /** Add one patient row to the authenticated user's Eligibility bookmarks. */
  addEligibilityBookmark: (trialId: string, mrn: string, signal?: AbortSignal) =>
    api.put<Record<string, never>, EligibilityBookmarkResponse>(
      API_ENDPOINTS.ELIGIBILITY_BOOKMARK(trialId, mrn),
      {},
      signal,
    ),

  /** Remove one patient row from the authenticated user's Eligibility bookmarks. */
  removeEligibilityBookmark: (
    trialId: string,
    mrn: string,
    signal?: AbortSignal,
  ) =>
    api.delete<EligibilityBookmarkResponse>(
      API_ENDPOINTS.ELIGIBILITY_BOOKMARK(trialId, mrn),
      signal,
    ),

  /** Mark one patient row read for the authenticated user. */
  markEligibilityRead: (trialId: string, mrn: string, signal?: AbortSignal) =>
    api.put<Record<string, never>, EligibilityReadStateResponse>(
      API_ENDPOINTS.ELIGIBILITY_READ_STATE(trialId, mrn),
      {},
      signal,
    ),

  /** Mark one patient row unread for the authenticated user. */
  markEligibilityUnread: (trialId: string, mrn: string, signal?: AbortSignal) =>
    api.delete<EligibilityReadStateResponse>(
      API_ENDPOINTS.ELIGIBILITY_READ_STATE(trialId, mrn),
      signal,
    ),

  /** Fetch one patient row's read state for the authenticated user. */
  getEligibilityReadState: (
    trialId: string,
    mrn: string,
    signal?: AbortSignal,
  ) =>
    api.get<EligibilityReadStateResponse>(
      API_ENDPOINTS.ELIGIBILITY_READ_STATE(trialId, mrn),
      signal,
    ),

  /** Create a patient note; omitted assignee defaults server-side to its creator. */
  createNote: (mrn: string, body: CreateNoteRequest, signal?: AbortSignal) =>
    api.post<CreateNoteRequest, ClinicalNoteResponse>(
      API_ENDPOINTS.PATIENT_NOTES(mrn),
      body,
      signal,
    ),

  /** Update editable note fields and append the corresponding audit activity. */
  updateNote: (noteId: string, body: UpdateNoteRequest, signal?: AbortSignal) =>
    api.patch<UpdateNoteRequest, ClinicalNoteResponse>(
      API_ENDPOINTS.NOTE_DETAIL(noteId),
      body,
      signal,
    ),

  /**
   * Soft-delete a note while retaining its audit record.
   *
   * The shared client currently attempts JSON parsing for an empty 204 response;
   * only that post-success syntax error is normalized to a successful void result.
   */
  deleteNote: async (noteId: string, signal?: AbortSignal): Promise<void> => {
    try {
      await api.delete<void>(API_ENDPOINTS.NOTE_DETAIL(noteId), signal);
    } catch (error: unknown) {
      if (!(error instanceof SyntaxError)) throw error;
    }
  },

  /** Append a rich-text reply to a note. */
  createNoteReply: (
    noteId: string,
    body: NoteReplyRequest,
    signal?: AbortSignal,
  ) =>
    api.post<NoteReplyRequest, ClinicalNoteResponse>(
      API_ENDPOINTS.NOTE_REPLIES(noteId),
      body,
      signal,
    ),

  /** Edit an existing reply and return its updated activity. */
  updateNoteReply: (
    noteId: string,
    activityId: string,
    body: NoteReplyRequest,
    signal?: AbortSignal,
  ) =>
    api.patch<NoteReplyRequest, ClinicalNoteResponse>(
      API_ENDPOINTS.NOTE_REPLY(noteId, activityId),
      body,
      signal,
    ),

  /** Soft-delete a reply while retaining lifecycle history. */
  deleteNoteReply: (noteId: string, activityId: string, signal?: AbortSignal) =>
    api.delete<ClinicalNoteResponse>(
      API_ENDPOINTS.NOTE_REPLY(noteId, activityId),
      signal,
    ),

  /** Execute an explicit lifecycle or shared-read command. */
  commandNote: (
    noteId: string,
    command: "resolve" | "reopen" | "read" | "unread",
    signal?: AbortSignal,
  ) =>
    api.post<Record<string, never>, ClinicalNoteResponse>(
      API_ENDPOINTS.NOTE_COMMAND(noteId, command),
      {},
      signal,
    ),

  /** Add one or more active participants to a note. */
  addNoteParticipants: (
    noteId: string,
    body: { user_ids: string[]; expected_version?: number },
    signal?: AbortSignal,
  ) =>
    api.post<typeof body, ClinicalNoteResponse>(
      API_ENDPOINTS.NOTE_PARTICIPANTS(noteId),
      body,
      signal,
    ),

  /** Remove an active participant from a note. */
  removeNoteParticipant: (
    noteId: string,
    userId: string,
    signal?: AbortSignal,
  ) =>
    api.delete<ClinicalNoteResponse>(
      API_ENDPOINTS.NOTE_PARTICIPANT(noteId, userId),
      signal,
    ),

  getNotifications: (q: NotificationsQuery, signal?: AbortSignal) =>
    api.post<NotificationsRequest, NotificationsResponse>(
      API_ENDPOINTS.NOTIFICATIONS,
      buildNotificationsBody(q),
      signal,
    ),

  /** Persist one notification document as read for the authenticated user. */
  markNotificationRead: (
    body: MarkNotificationReadRequest,
    signal?: AbortSignal,
  ) =>
    api.post<MarkNotificationReadRequest, MarkNotificationReadResponse>(
      API_ENDPOINTS.MARK_NOTIFICATION_READ,
      body,
      signal,
    ),

  /** Return one notification document to unread for the authenticated user. */
  markNotificationUnread: (
    body: MarkNotificationReadRequest,
    signal?: AbortSignal,
  ) =>
    api.post<MarkNotificationReadRequest, MarkNotificationUnreadResponse>(
      API_ENDPOINTS.MARK_NOTIFICATION_UNREAD,
      body,
      signal,
    ),

  /** Fetch personalized unread report counts for each document category. */
  getTabCounts: (date: string, signal?: AbortSignal) =>
    api.get<TabCountsResponse>(
      `${API_ENDPOINTS.GET_TAB_COUNTS}?date=${encodeURIComponent(date)}`,
      signal,
    ),

  /** Fetch filter facet option lists + counts for a date (populates FilterControls). */
  getFacetCounts: (date: string, docType?: string, signal?: AbortSignal) =>
    api.get<FacetCountsResponse>(
      `${API_ENDPOINTS.GET_FACETS}?date=${encodeURIComponent(date)}${
        docType ? `&doc_type=${encodeURIComponent(docType)}` : ""
      }`,
      signal,
    ),

  /** Fetch the full enriched patient record for the Document Summary header. */
  getPatientDetail: (mrn: string, signal?: AbortSignal) =>
    api.get<PatientDetailResponse>(
      API_ENDPOINTS.GET_PATIENT_DETAIL(mrn),
      signal,
    ),

  /** Replace a patient's free-form CRC tag list (persisted server-side). */
  updatePatientTags: (mrn: string, tags: string[], signal?: AbortSignal) =>
    api.post<PatientTagsUpdate, PatientDetailResponse>(
      API_ENDPOINTS.UPDATE_PATIENT_TAGS(mrn),
      { tags },
      signal,
    ),

  getDocumentSummary: (
    patientMrn: string,
    docType: string,
    date?: string,
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams({
      patient_mrn: patientMrn,
      doc_type: docType,
    });
    if (date) params.set("date", date);
    return api.get<DocumentSummaryResponse>(
      `${API_ENDPOINTS.GET_DOCUMENT_SUMMARY}?${params.toString()}`,
      signal,
    );
  },

  getDocumentPdfUrl: (documentId: string, signal?: AbortSignal) =>
    api.get<PdfUrlResponse>(API_ENDPOINTS.GET_DOCUMENT_PDF(documentId), signal),

  getSourcePdfUrl: (
    patientMrn: string,
    docType: string,
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams({
      patient_mrn: patientMrn,
      doc_type: docType,
    });
    return api.get<PdfUrlResponse>(
      `${API_ENDPOINTS.GET_SOURCE_PDF}?${params.toString()}`,
      signal,
    );
  },

  /** Fire a scoped summarization run (from MRNs or a CSV); returns a run id. */
  runSummarization: (body: RunSummarizationRequest, signal?: AbortSignal) =>
    api.post<RunSummarizationRequest, JobAcceptedResponse>(
      API_ENDPOINTS.RUN_SUMMARIZATION,
      body,
      signal,
    ),

  /** Archive and validate the original CSV server-side before starting ingestion. */
  uploadPatientCsv: (
    file: File,
    options: PatientCsvUploadOptions,
    signal?: AbortSignal,
  ) => {
    const form = new FormData();
    form.append("file", file);
    form.append("csv_format", options.csv_format);
    if (options.date) form.append("date", options.date);
    if (options.skip_backfill !== undefined)
      form.append("skip_backfill", String(options.skip_backfill));
    if (options.summarize !== undefined)
      form.append("summarize", String(options.summarize));
    return api.post<FormData, JobAcceptedResponse>(
      API_ENDPOINTS.RUN_SUMMARIZATION,
      form,
      signal,
    );
  },

  /** Fetch newest-first manual ingestion job history. */
  getRunHistory: (offset = 0, limit = 20, signal?: AbortSignal) =>
    api.get<JobHistoryResponse>(
      `${API_ENDPOINTS.GET_RUN_HISTORY}?offset=${offset}&limit=${limit}`,
      signal,
    ),

  /** Poll the status of a summarization run by its run id. */
  getRunStatus: (runId: string, signal?: AbortSignal) =>
    api.get<JobStatusResponse>(API_ENDPOINTS.GET_RUN_STATUS(runId), signal),

  /** Resume unfinished checkpoints in the same interrupted parent run. */
  resumeRun: (runId: string, signal?: AbortSignal) =>
    api.post<Record<string, never>, JobStatusResponse>(
      API_ENDPOINTS.RESUME_RUN(runId),
      {},
      signal,
    ),

  /** Fetch a page of retryable and permanent per-MRN failures. */
  getRunMrnFailures: (
    runId: string,
    offset = 0,
    limit = 50,
    signal?: AbortSignal,
  ) =>
    api.get<JobMrnOutcomePage>(
      `${API_ENDPOINTS.GET_RUN_MRNS(runId)}?offset=${offset}&limit=${limit}`,
      signal,
    ),

  /** Fetch one page of the Eligibility Notification table for a date + trial. */
  getEligibilityRows: (body: EligibilityRowsRequest, signal?: AbortSignal) =>
    api.post<EligibilityRowsRequest, EligibilityRowsResponse>(
      API_ENDPOINTS.ELIGIBILITY_ROWS,
      body,
      signal,
    ),

  /** Fetch one page of every patient ever screened for a trial, newest screening first. */
  getScreenedEligibilityRows: (
    body: EligibilityScreenedRowsRequest,
    signal?: AbortSignal,
  ) =>
    api.post<EligibilityScreenedRowsRequest, EligibilityRowsResponse>(
      API_ENDPOINTS.ELIGIBILITY_SCREENED_ROWS,
      body,
      signal,
    ),

  /**
   * Queue a whole cohort for screening from a CSV of MRNs.
   *
   * Sent as multipart rather than JSON because the endpoint takes the file itself, so
   * the raw upload is what the server parses and can archive.
   */
  startCohortScreening: async (
    trialId: string,
    file: File,
    signal?: AbortSignal,
  ) => {
    const token = await getBearerToken();
    if (!token) throw new Error("Failed to retrieve bearer token");
    const form = new FormData();
    form.append("file", file);
    const response = await fetch(API_ENDPOINTS.SCREENING_BATCH(trialId), {
      method: "POST",
      body: form,
      signal,
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      // The server explains a refused cohort in `detail` (an unusable file, or more
      // never-ingested patients than one upload can enrich), and that reason is the
      // whole value of the error — a bare status code leaves the user with no next step.
      const detail = await response
        .json()
        .then((body) => (body as { detail?: string })?.detail)
        .catch(() => undefined);
      throw new Error(detail || `Cohort screening failed (${response.status})`);
    }
    return (await response.json()) as ScreeningBatchResponse;
  },

  /** Persist one patient's prescreening state change for a trial. */
  patchEligibilityState: (
    trialId: string,
    mrn: string,
    body: EligibilityPatchRequest,
    signal?: AbortSignal,
  ) =>
    api.patch<EligibilityPatchRequest, EligibilityPatchResponse>(
      API_ENDPOINTS.ELIGIBILITY_PATCH(trialId, mrn),
      body,
      signal,
    ),

  /**
   * Link a patient on the source trial to another subscribed trial worklist.
   *
   * @param sourceTrialId - Trial whose worklist row initiated the link
   * @param mrn - Patient medical record number
   * @param targetTrialId - Subscribed trial to monitor
   * @param signal - Optional request cancellation signal
   * @returns Typed trial link and the target-trial eligibility row
   */
  addMonitoringTrial: (
    sourceTrialId: string,
    mrn: string,
    targetTrialId: string,
    signal?: AbortSignal,
  ) =>
    api.post<{ target_trial_id: string }, MonitoringTrialResponse>(
      API_ENDPOINTS.ELIGIBILITY_MONITORING_TRIALS(sourceTrialId, mrn),
      { target_trial_id: targetTrialId },
      signal,
    ),

  /** Screen one patient against one trial; returns as soon as the run is queued. */
  analyzeEligibilityPatient: (
    trialId: string,
    mrn: string,
    signal?: AbortSignal,
  ) =>
    api.post<Record<string, never>, EligibilityAnalyzeResponse>(
      API_ENDPOINTS.ELIGIBILITY_ANALYZE(trialId, mrn),
      {},
      signal,
    ),

  /**
   * Queue up to 25 patients for screening.
   *
   * The worklist dialog posts a comma-separated list here. The per-patient route
   * stays for a single re-screen, such as after a manual override.
   */
  analyzeEligibilityPatients: (
    trialId: string,
    mrns: string[],
    signal?: AbortSignal,
  ) =>
    api.post<{ mrns: string[] }, ScreeningBatchResponse>(
      API_ENDPOINTS.ELIGIBILITY_ANALYZE_MANY(trialId),
      { mrns },
      signal,
    ),

  /** Queue criteria-only evaluation for one or more patients. */
  analyzeCriteriaEligibility: (
    trialId: string,
    body: CriteriaEvaluationAnalyzeRequest,
    signal?: AbortSignal,
  ) =>
    api.post<CriteriaEvaluationAnalyzeRequest, ScreeningBatchResponse>(
      API_ENDPOINTS.ELIGIBILITY_ANALYZE_MANY(trialId),
      body,
      signal,
    ),

  /** Fetch one patient's screening results for a trial, newest first. */
  getEligibilityScreening: (
    trialId: string,
    mrn: string,
    signal?: AbortSignal,
  ) =>
    api.get<EligibilityScreeningHistory>(
      API_ENDPOINTS.ELIGIBILITY_SCREENING(trialId, mrn),
      signal,
    ),

  /**
   * Fetch the single-patient eligibility details payload.
   *
   * Includes the worklist row, patient strips, latest nested screening,
   * recommended actions, and overrides. Callers must not also GET `/overrides`
   * on first paint.
   */
  getEligibilityDetail: (
    trialId: string,
    mrn: string,
    signal?: AbortSignal,
  ) =>
    api.get<EligibilityDetailResponse>(
      API_ENDPOINTS.ELIGIBILITY_DETAIL(trialId, mrn),
      signal,
    ),

  /** Fetch criteria lifecycle state and the latest completed criteria result. */
  getCriteriaEligibilityDetail: (
    trialId: string,
    mrn: string,
    signal?: AbortSignal,
  ) =>
    api.get<CriteriaScreeningResponse>(
      API_ENDPOINTS.ELIGIBILITY_CRITERIA_DETAIL(trialId, mrn),
      signal,
    ),

  /** Fetch status transitions only when the status-history sub-view opens. */
  getEligibilityStatusHistory: (
    trialId: string,
    mrn: string,
    signal?: AbortSignal,
  ) =>
    api.get<PatientStatusHistoryResponse>(
      API_ENDPOINTS.ELIGIBILITY_STATUS_HISTORY(trialId, mrn),
      signal,
    ),

  /**
   * Apply a server-owned override intent (mark met / mark ineligible) for one action.
   */
  applyEligibilityOverrideIntent: (
    trialId: string,
    mrn: string,
    actionId: string,
    body: {
      intent: "MARK_INELIGIBLE" | "MARK_MET_AND_CONTINUE";
      comment: string;
      justification_pdf_ref?: string | null;
    },
    signal?: AbortSignal,
  ) =>
    api.post<typeof body, Record<string, unknown>>(
      API_ENDPOINTS.ELIGIBILITY_OVERRIDE_INTENT(trialId, mrn, actionId),
      body,
      signal,
    ),

  /**
   * Resolve one recommended action as a linked task note or an existing schedule match.
   */
  createEligibilityTask: (
    trialId: string,
    mrn: string,
    actionId: string,
    body: CreateEligibilityTaskRequest = {},
    signal?: AbortSignal,
  ) =>
    api.post<CreateEligibilityTaskRequest, CreateEligibilityTaskResponse>(
      API_ENDPOINTS.ELIGIBILITY_CREATE_TASK(trialId, mrn, actionId),
      body,
      signal,
    ),

  /** Create or replace one CRC atom override for a patient. */
  setEligibilityAtomOverride: (
    trialId: string,
    mrn: string,
    atomId: string,
    body: {
      verdict: string;
      comment: string;
      justification_pdf_ref?: string | null;
    },
    signal?: AbortSignal,
  ) =>
    api.put<typeof body, ScreeningOverride>(
      API_ENDPOINTS.ELIGIBILITY_ATOM_OVERRIDE(trialId, mrn, atomId),
      body,
      signal,
    ),

  /**
   * Upload a justification PDF bound to a recommended screening action.
   *
   * @param trialId - Trial the patient is evaluated against
   * @param mrn - Patient medical record number
   * @param actionId - Recommended-action handle from eligibility detail
   * @param file - PDF upload
   * @param signal - Optional request cancellation signal
   * @returns Opaque PDF reference for override persistence
   */
  uploadOverrideJustification: async (
    trialId: string,
    mrn: string,
    actionId: string,
    file: File,
    signal?: AbortSignal,
  ) => {
    const token = await getBearerToken();
    if (!token) {
      throw new Error("Failed to retrieve bearer token");
    }
    const form = new FormData();
    form.append("file", file);
    const response = await fetch(
      API_ENDPOINTS.ELIGIBILITY_OVERRIDE_JUSTIFICATION_UPLOAD(
        trialId,
        mrn,
        actionId,
      ),
      {
        method: "POST",
        body: form,
        signal,
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (!response.ok) {
      const detail = await response
        .json()
        .then((body) => (body as { detail?: string })?.detail)
        .catch(() => undefined);
      throw new Error(
        detail || `Justification upload failed (${response.status})`,
      );
    }
    return (await response.json()) as {
      justification_pdf_ref: string;
      source_path: string;
      file_name: string;
    };
  },

  /**
   * Resolve an authenticated relative PDF path into its short-lived signed URL.
   *
   * @param sourcePath - Same-service `/api/v1/...` path from a question, atom, or override
   * @param signal - Optional request cancellation signal
   * @returns Signed PDF URL or structured document, or rejection for unsafe paths
   */
  getEligibilitySourcePdf: (
    sourcePath: string,
    signal?: AbortSignal,
  ): Promise<EligibilitySourceResponse> => {
    if (!sourcePath.startsWith("/api/v1/")) {
      return Promise.reject(
        new Error("Eligibility PDF source must be a relative /api/v1 path"),
      );
    }
    if (sourcePath.split("/").some((segment) => segment === "..")) {
      return Promise.reject(
        new Error(
          "Eligibility PDF source must not contain parent-directory segments",
        ),
      );
    }
    return api.get<EligibilitySourceResponse>(
      API_ENDPOINTS.AUTHENTICATED_CLINICAL_TRIALS_PATH(sourcePath),
      signal,
    );
  },

  /** List facility-owned trials for Protocol View (superadmin only). */
  listTrials: (includeArchived = false, signal?: AbortSignal) =>
    api.get<{ trials: ProtocolTrial[] }>(
      `${API_ENDPOINTS.TRIALS}?include_archived=${includeArchived}`,
      signal,
    ),

  /** Create a facility-owned trial. */
  createTrial: (
    body: {
      confirmation: boolean;
      display_name: string;
      sponsor_protocol_id: string;
      nct_id?: string;
      sponsor?: string;
      region?: string;
    },
    signal?: AbortSignal,
  ) => api.post<typeof body, ProtocolTrial>(API_ENDPOINTS.TRIALS, body, signal),

  /** Load one facility-owned trial. */
  getTrial: (trialId: string, signal?: AbortSignal) =>
    api.get<ProtocolTrial>(API_ENDPOINTS.TRIAL_DETAIL(trialId), signal),

  /** Archive a facility-owned trial. */
  archiveTrial: (
    trialId: string,
    body: { confirmation: boolean; expected_version: number },
    signal?: AbortSignal,
  ) =>
    api.post<typeof body, ProtocolTrial>(
      API_ENDPOINTS.TRIAL_ARCHIVE(trialId),
      body,
      signal,
    ),

  /** Restore an archived facility-owned trial. */
  restoreTrial: (
    trialId: string,
    body: { confirmation: boolean; expected_version: number },
    signal?: AbortSignal,
  ) =>
    api.post<typeof body, ProtocolTrial>(
      API_ENDPOINTS.TRIAL_RESTORE(trialId),
      body,
      signal,
    ),

  /** Preview a human-authored protocol CSV import. */
  previewProtocolImport: async (
    trialId: string,
    file: File,
    signal?: AbortSignal,
  ) => {
    const token = await getBearerToken();
    if (!token) throw new Error("Failed to retrieve bearer token");
    const form = new FormData();
    form.append("file", file);
    const response = await fetch(API_ENDPOINTS.TRIAL_IMPORT_PREVIEW(trialId), {
      method: "POST",
      body: form,
      signal,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    if (!response.ok) {
      throw new Error(`Import preview failed (${response.status})`);
    }
    return (await response.json()) as ProtocolImportPreview;
  },

  /** Confirm a CSV import preview into a draft. */
  confirmProtocolImport: (
    trialId: string,
    importId: string,
    body: { confirmation: boolean; replace_draft_id?: string },
    signal?: AbortSignal,
  ) =>
    api.post<typeof body, ProtocolDraft>(
      API_ENDPOINTS.TRIAL_IMPORT_CONFIRM(trialId, importId),
      body,
      signal,
    ),

  /** Load a draft bundle for table/DAG views. */
  getProtocolDraft: (trialId: string, draftId: string, signal?: AbortSignal) =>
    api.get<ProtocolDraftBundle>(
      API_ENDPOINTS.TRIAL_DRAFT(trialId, draftId),
      signal,
    ),

  /** Update one criterion in an editable protocol draft. */
  upsertProtocolCriterion: (
    trialId: string,
    draftId: string,
    body: {
      confirmation: boolean;
      expected_revision: number;
      criterion: ProtocolCriterion;
    },
    signal?: AbortSignal,
  ) =>
    api.post<typeof body, ProtocolDraft>(
      API_ENDPOINTS.TRIAL_DRAFT_CRITERIA(trialId, draftId),
      body,
      signal,
    ),

  /** Validate the current draft revision. */
  validateProtocolDraft: (
    trialId: string,
    draftId: string,
    signal?: AbortSignal,
  ) =>
    api.post<Record<string, never>, ProtocolValidationRun>(
      API_ENDPOINTS.TRIAL_DRAFT_VALIDATE(trialId, draftId),
      {},
      signal,
    ),

  /** List deterministic missing-connection suggestions. */
  getConnectionSuggestions: (
    trialId: string,
    draftId: string,
    signal?: AbortSignal,
  ) =>
    api.get<{ suggestions: ProtocolConnection[] }>(
      API_ENDPOINTS.TRIAL_DRAFT_SUGGESTIONS(trialId, draftId),
      signal,
    ),

  /** Add a draft connection. */
  addProtocolConnection: (
    trialId: string,
    draftId: string,
    body: {
      confirmation: boolean;
      expected_revision: number;
      connection: ProtocolConnection;
    },
    signal?: AbortSignal,
  ) =>
    api.post<typeof body, ProtocolDraft>(
      API_ENDPOINTS.TRIAL_DRAFT_CONNECTIONS(trialId, draftId),
      body,
      signal,
    ),

  /** Delete a draft connection. */
  deleteProtocolConnection: (
    trialId: string,
    draftId: string,
    connectionId: string,
    body: { confirmation: boolean; expected_revision: number },
    signal?: AbortSignal,
  ) =>
    api.post<typeof body, ProtocolDraft>(
      API_ENDPOINTS.TRIAL_DRAFT_CONNECTION_DELETE(
        trialId,
        draftId,
        connectionId,
      ),
      body,
      signal,
    ),

  /** Run a draft review/publish lifecycle action. */
  protocolDraftAction: (
    trialId: string,
    draftId: string,
    action:
      "request-review" | "request-changes" | "approve" | "publish" | "discard",
    body: { confirmation: boolean; comment?: string },
    signal?: AbortSignal,
  ) =>
    api.post<typeof body, ProtocolDraft | ProtocolVersion>(
      API_ENDPOINTS.TRIAL_DRAFT_REVIEW(trialId, draftId, action),
      body,
      signal,
    ),

  /** List published protocol versions. */
  listProtocolVersions: (trialId: string, signal?: AbortSignal) =>
    api.get<ProtocolVersion[]>(API_ENDPOINTS.TRIAL_VERSIONS(trialId), signal),

  /** List trial audit events. */
  listProtocolAudit: (trialId: string, signal?: AbortSignal) =>
    api.get<{ events: ProtocolAuditEvent[] }>(
      API_ENDPOINTS.TRIAL_AUDIT(trialId),
      signal,
    ),

  /** List T1 evaluation runs for a trial. */
  listT1Runs: (trialId: string, signal?: AbortSignal) =>
    api.get<{ runs: T1EvaluationRun[] }>(
      API_ENDPOINTS.TRIAL_T1_RUNS(trialId),
      signal,
    ),

  /** Get one T1 evaluation run. */
  getT1Run: (trialId: string, runId: string, signal?: AbortSignal) =>
    api.get<T1EvaluationRun>(
      API_ENDPOINTS.TRIAL_T1_RUN(trialId, runId),
      signal,
    ),

  /** Get the materialized T1 funnel for a run. */
  getT1Funnel: (trialId: string, runId: string, signal?: AbortSignal) =>
    api.get<{ funnel: T1FunnelSnapshot; run: T1EvaluationRun }>(
      API_ENDPOINTS.TRIAL_T1_FUNNEL(trialId, runId),
      signal,
    ),

  /** List patient dispositions for a T1 run. */
  listT1Patients: (
    trialId: string,
    runId: string,
    params?: { disposition?: string; dropout_node_id?: string },
    signal?: AbortSignal,
  ) => {
    const query = new URLSearchParams();
    if (params?.disposition) query.set("disposition", params.disposition);
    if (params?.dropout_node_id)
      query.set("dropout_node_id", params.dropout_node_id);
    const suffix = query.toString() ? `?${query.toString()}` : "";
    return api.get<{ patients: T1PatientEvaluation[] }>(
      `${API_ENDPOINTS.TRIAL_T1_PATIENTS(trialId, runId)}${suffix}`,
      signal,
    );
  },

  /**
   * Fetch eligibility option catalogs and counts for the selected trial.
   *
   * @param trialId - Selected trial used to populate tab counts
   * @param signal - Optional request cancellation signal
   * @returns Eligibility option catalogs
   */
  getEligibilityOptions: (trialId?: string, signal?: AbortSignal) =>
    api.get<EligibilityOptionsResponse>(
      trialId
        ? `${API_ENDPOINTS.ELIGIBILITY_OPTIONS}?trial_id=${encodeURIComponent(trialId)}`
        : API_ENDPOINTS.ELIGIBILITY_OPTIONS,
      signal,
    ),

  /** Fetch one filtered, sorted page of screened eligibility patients. */
  getEligibilityScreenedRows: (
    query: EligibilityScreenedRowsQuery,
    signal?: AbortSignal,
  ) =>
    api.post<EligibilityScreenedRowsRequest, EligibilityScreenedRowsResponse>(
      API_ENDPOINTS.ELIGIBILITY_SCREENED_ROWS,
      buildEligibilityScreenedRowsBody(query),
      signal,
    ),

  /** Get one patient's T1 atom trace. */
  getT1PatientTrace: (
    trialId: string,
    runId: string,
    mrn: string,
    signal?: AbortSignal,
  ) =>
    api.get<{ patient: T1PatientEvaluation; atoms: T1AtomEvaluation[] }>(
      API_ENDPOINTS.TRIAL_T1_PATIENT(trialId, runId, mrn),
      signal,
    ),

  /* ---------------------------- agentic screening ---------------------------- */

  /** List every workflow the orchestrator can run. */
  listScreeningWorkflows: (signal?: AbortSignal) =>
    api.get<ScreeningWorkflowSummary[]>(
      API_ENDPOINTS.SCREENING_WORKFLOWS,
      signal,
    ),

  /** Fetch one workflow version, including the JSON Schema for its arguments. */
  getScreeningWorkflow: (
    name: string,
    version?: number,
    signal?: AbortSignal,
  ) =>
    api.get<ScreeningWorkflowDetail>(
      API_ENDPOINTS.SCREENING_WORKFLOW(name, version),
      signal,
    ),

  /** Queue a screening run; returns immediately with a run id to follow. */
  triggerScreeningRun: (body: ScreeningRunRequest, signal?: AbortSignal) =>
    api.post<ScreeningRunRequest, ScreeningRunResponse>(
      API_ENDPOINTS.SCREENING_RUNS,
      body,
      signal,
    ),

  /** List screening runs, newest first. */
  listScreeningRuns: (signal?: AbortSignal) =>
    api.get<ScreeningRunStatus[]>(API_ENDPOINTS.SCREENING_RUNS, signal),

  /** Fetch one screening run's current state. */
  getScreeningRun: (runId: string, signal?: AbortSignal) =>
    api.get<ScreeningRunStatus>(API_ENDPOINTS.SCREENING_RUN(runId), signal),

  /** Fetch a screening run's collected result rows. */
  getScreeningResults: (runId: string, signal?: AbortSignal) =>
    api.get<ScreeningRunResults>(
      API_ENDPOINTS.SCREENING_RUN_RESULTS(runId),
      signal,
    ),

  /**
   * Follow a run's server-sent event stream, invoking `onEvent` per record.
   *
   * `EventSource` cannot carry an `Authorization` header and these routes are
   * superadmin-gated, so this reads the response body itself: `fetch` with the
   * bearer token, then a `ReadableStream` reader that reassembles SSE frames.
   * The stream replays the run from its start before following it live, so
   * attaching late — or after the run finished — still shows the whole trace.
   *
   * @param runId - Run identifier
   * @param onEvent - Called once per `data:` record
   * @param signal - Aborts the stream (e.g. on unmount)
   * @param item - Fan-out item to filter to; run-level events always come through
   */
  streamScreeningLogs: async (
    runId: string,
    onEvent: (event: ScreeningLogEvent) => void,
    signal?: AbortSignal,
    item?: string,
  ): Promise<void> => {
    const token = await getBearerToken();
    if (!token) throw new Error("Failed to retrieve bearer token");
    const response = await fetch(
      API_ENDPOINTS.SCREENING_RUN_LOGS(runId, item),
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        signal,
      },
    );
    if (!response.ok || !response.body) {
      throw new Error(`Log stream failed (${response.status})`);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line; keep the trailing partial one.
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";
      for (const frame of frames) {
        const data = frame
          .split("\n")
          .filter((line) => line.startsWith("data:"))
          .map((line) => line.slice(5).trim())
          .join("");
        if (!data || data === "{}") continue;
        try {
          onEvent(JSON.parse(data) as ScreeningLogEvent);
        } catch {
          // A malformed frame is not worth killing the stream over.
        }
      }
    }
  },
};
